import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Card } from "primereact/card";
import REIDataTable from "../../../components/ui/dataTable";
import REIEditor from "../../../components/shared/editor";
import { REIButton, REIProgressSpinner } from "../../../components/ui";
import ViewPropertyDetails, { Links, TableData } from "../../../interface/Watchlist/propertyDetails";
import {
  ARCHISTAR_URL,
  ArchistrarDataMap,
  ColumnType,
  PAUnitStasticMap,
  UnitStasticMap,
} from "../../../utils/constants";
import SuburbStatastics from "../../../utils/dataMapper";
import * as styles from "./PropertyInformation.module.scss";
import { useHttpGets } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import DataMapper from "../../../utils/dataMapper";
import { RootState } from "../../../store";
import AdvancedFileUpload from "../../../components/shared/AdvancedFileUpload";
import EditorAnalyser from "../../ProppertyAnalyser/Property Summary/AnalyserNotes";
import { useNavigate } from "react-router";

interface IPropertyDescription {
  propertyDescription: ViewPropertyDetails;
}

interface ArchistrarData {
  Archistar_Activated: boolean;
  LandUses: string[];
  MaxDensity: string;
  MaxHeight: string;
  MaxStories: string;
  Overlays: string[];
  Zoning: string;
}

interface ArchistrarUIColumns {
  LandUses: string;
  Overlays: string;
  Zoning: string;
}

const PropertyDescription: React.FC<IPropertyDescription> = ({ propertyDescription }) => {
  const [unitSales, setUnitSales] = useState<TableData[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const archistrarMapperData = useRef<TableData[] | null>(null);
  const archistarTableData = useRef<ArchistrarUIColumns>({} as ArchistrarUIColumns);
  const archistarData = useRef<ArchistrarData>({} as ArchistrarData);
  const resources = useRef<Links[]>(propertyDescription.PropertyLinks.map((data) => data.Links).map((link) => link));
  const showNotesAndEditor = useSelector((state: RootState) => state.propertyDetails.showFileUploadNotes);
  const filterData = useSelector((state: RootState) => state.investar.filterValue);
  let descriptionText: { __html: string } = { __html: "" };
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    async function fetchData() {
      {
        const tableData = propertyDescription.isFromPropertyAnalyser
          ? propertyDescription.PAMarketingStatstics
          : propertyDescription.SuburbStatistics;
        const labelMap = propertyDescription.isFromPropertyAnalyser ? PAUnitStasticMap : UnitStasticMap;

        const data = SuburbStatastics({
          tableData: tableData,
          labelMap: labelMap,
          propertyDetails: propertyDescription.PropertyDetails,
        });

        const orderedData = Array.from(labelMap.values()).map((key) => {
          const matchingItem = data.find((item) => item.title === key);
          return matchingItem || { key, title: labelMap.get(key), value: "N/A" };
        });

        if (orderedData != undefined) {
          setLoading(false);
          setUnitSales(orderedData);
          if (!propertyDescription.isFromPropertyAnalyser) {
            await fetchArchistrarData();
          }
        }
      }
    }

    async function fetchArchistrarData() {
      {
        setLoading(true);
        await useHttpGets(
          `${ApiEndPoints.searchSummary}AU/search/ArchistarStats?MemberID=${propertyDescription.PropertyDetails.MemberId}&PropertyCountryCode=${propertyDescription.PropertyDetails.CountryID}&StreetAddress=${propertyDescription.PropertyDetails.StreetAddress}&Suburb=${propertyDescription.PropertyDetails.Suburb}&State=${propertyDescription.PropertyDetails.State}&Postcode=${propertyDescription.PropertyDetails.Postcode}&_=1719314481954`,
          undefined
        )
          .then((result) => {
            if (result.status == 200) {
              if (result.data !== null) {
                archistarData.current = result.data;
                archistarTableData.current = {
                  Zoning: archistarData.current?.Zoning ?? "",
                  LandUses: archistarData.current?.LandUses?.at(0) ?? "",
                  Overlays: archistarData.current?.Overlays?.at(0) ?? "",
                };
              }
              archistrarMapperData.current = DataMapper({
                tableData: archistarTableData.current,
                labelMap: ArchistrarDataMap,
              });
            } else {
              return <REIProgressSpinner></REIProgressSpinner>;
            }
          })
          .catch(() => {});
        setLoading(false);
      }
    }

    if (propertyDescription != undefined) {
      fetchData();
    }
  }, [propertyDescription.PropertyDetails.PropertyId]);

  const resourcesColumns = [
    {
      field: "UrlDescription",
      header: ColumnType.resouceLinks,
      icon: "",
      type: ColumnType.link,
    },
  ];

  const unitSalesColumns = [
    {
      field: "title",
      header: "Berrimah suburb stats",
      position: "left",
    },
    {
      field: "data",
      header: "",
      position: "right",
    },
  ];

  const archistrarColumns = [
    {
      field: "title",
      header: "",
      position: "left",
    },
    {
      field: "data",
      header: "",
      position: "right",
    },
  ];

  const highlightWords = (): { __html: string } => {
    let keyWords = filterData?.Country?.StrategyTypeKeywords?.filter((kw) => kw.keyword) || [];
    let strategyKeyWords =
      filterData?.Country?.Strategy &&
      filterData.Country.Strategy.length > 0 &&
      filterData.Country.Strategy[0]?.keywords &&
      filterData.Country.Strategy[0].keywords.length > 0 &&
      filterData.Country.Strategy[0].keywords
        .toString()
        .split(",")
        .map((s) => s.trim());

    let html = descriptionText.__html;

    if ((keyWords && keyWords.length > 0) || (strategyKeyWords && strategyKeyWords.length > 0)) {
      keyWords = keyWords.map((kw) => kw.keyword);
      keyWords = keyWords.concat(strategyKeyWords);

      const regex = new RegExp(`\\b(${keyWords.join("|")})\\b`, "gi");

      // Use DOMParser to safely manipulate HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.nodeType === Node.TEXT_NODE) {
          const replacedText = node.nodeValue?.replace(regex, (match) => {
            return `<span class="${styles.highlight}">${match}</span>`;
          });
          if (replacedText !== node.nodeValue) {
            const span = document.createElement("span");
            span.innerHTML = replacedText || "";
            (node as ChildNode).replaceWith(span);
          }
        }
      }

      html = doc.body.innerHTML;
    }
    return { __html: html };
  };

  descriptionText = {
    __html: propertyDescription.PropertyDetails.Description ?? "",
  };

  const titleText = {
    __html: (
      <span
        dangerouslySetInnerHTML={{
          __html: propertyDescription.PropertyDetails.Title.replace(/^,|,$/g, ""),
        }}
      ></span>
    ),
  };

  return (
    <>
      {loading && (
        <div className="flex items-center">
          <REIProgressSpinner></REIProgressSpinner>
        </div>
      )}
      {!loading && (
        <div className="flex md:flex-row flex-col">
          <div className="flex-none w-full md:w-2/3">
            {propertyDescription.PropertyDetails.Description && (
              <>
                <Card title="Property Description">
                  <span className="text-xs md:text-sm font-semibold -mt-1">{titleText.__html}</span>
                  <p
                    className="justify-start text-gray-800 text-justify text-[10px] md:text-sm p-1 leading-6"
                    dangerouslySetInnerHTML={highlightWords()}
                  ></p>
                </Card>
              </>
            )}
            {showNotesAndEditor && (
              <>
                <AdvancedFileUpload propertyData={propertyDescription.PropertyDetails.WatchlistID}></AdvancedFileUpload>
                {!propertyDescription.isFromPropertyAnalyser && (
                  <REIEditor propertyDetails={propertyDescription.PropertyDetails}></REIEditor>
                )}
                {propertyDescription.isFromPropertyAnalyser && (
                  <EditorAnalyser watchListId={propertyDescription.PropertyDetails.WatchlistID ?? ""}></EditorAnalyser>
                )}
              </>
            )}
          </div>
          <div className="flex-initial w-full md:w-1/3">
            <Card
              className="mb-4 md:ml-4"
              title={`${propertyDescription.isFromPropertyAnalyser ? "References" : "Resources"}`}
            >
              <div className="panel-box ">
                <REIDataTable data={resources.current} columns={resourcesColumns} customclass="hide-datatable-header" />
              </div>
            </Card>
            <Card
              title={`${propertyDescription.PropertyDetails.Suburb} suburb stats`}
              header=""
              className="mb-4 md:ml-4"
            >
              <div className="panel-box">
                <REIDataTable data={unitSales} columns={unitSalesColumns} customclass="hide-datatable-header" />
              </div>
            </Card>
            {!propertyDescription.isFromPropertyAnalyser && (
              <Card title="Planning Essentials - Powered by Archistar©" header="" className="mb-4 md:ml-4">
                <div className="panel-box">
                  <REIDataTable
                    data={archistrarMapperData.current ?? []}
                    columns={archistrarColumns}
                    customclass="hide-datatable-header"
                  ></REIDataTable>
                </div>
                <div className="">
                  <div className="text-[10px] md:text-xs pl-3 pt-1 pb-1"> See Disclaimer 2 </div>
                  <p className="text-xs md:text-sm pl-2">To access more information</p>
                  <p className="text-xs md:text-sm pl-2">
                    {!propertyDescription.isFromPropertyAnalyser && archistarData?.current?.Archistar_Activated ? (
                      <REIButton
                        id="wld-btn-loginarchistar"
                        onClick={() => {
                          window.open(ARCHISTAR_URL, "_blank", "noopener,noreferrer");
                        }}
                        classNames="border-0 text-white font-semibold py-1 px-4 md:py-2 md:px-6 rounded text-xs md:text-sm"
                        label="Log into ArchiStar"
                      />
                    ) : (
                      <REIButton
                        id="wld-btn-visitarchistar"
                        onClick={() => {
                          navigate("/archistar");
                        }}
                        classNames="border-0 text-white font-semibold py-1 px-4 md:py-2 md:px-6 rounded text-xs md:text-sm"
                        label="Visit ArchiStar"
                      />
                    )}
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PropertyDescription;
